<template>
  <div :class="audioPlayerPosition">
    <div
      v-if="audioInfo.inboundNumber && recording" 
      class="audio-player__inbound-number"
    >
      {{ $t('INBOUND_NUMBER') + ' ' + audioInfo.inboundNumber }}
    </div>
    <a
      target="javascript:void(0)" 
      class="audio-player__close btn btn-function"
      @click="hidePlayer"
    >
      <i class="fa fa-close f-s-13 p-r-0" />
    </a>
    <div class="audio-player__controls">
      <a
        href="javascript:void(0);" 
        :disabled="paused" 
        class="btn btn-function"
        @click="fastForwardOrBackward('backward', true)" 
        @mousedown="fastForwardOrBackward('backward', false)"
        @mouseup="resumePlay()"
      >
        <i class="fa fa-backward" />
      </a>
      <a
        v-if="!paused && currentTime > 0" 
        href="javascript:void(0);" 
        class="btn btn-function" 
        @click="pause()"
      >
        <i class="fa fa-pause" />
      </a>

      <a
        v-if="paused || currentTime === 0" 
        href="javascript:void(0);" 
        class="btn btn-function" 
        @click="play()"
      >
        <i class="fa fa-play" />
      </a>

      <a
        href="javascript:void(0);" 
        :disabled="paused && currentTime > 0"
        class="btn btn-function" 
        @click="fastForwardOrBackward('forward', true)"
        @mousedown="fastForwardOrBackward('forward', false)"
        @mouseup="resumePlay()"
      >
        <i class="fa fa-forward" />
      </a>

      <a
        href="javascript:void(0);" 
        :disabled="paused && currentTime > 0" 
        class="btn btn-function audio-player__playback-stack"
        @click="setPlaybackRate()"
      >
        <i class="fa fa-undo fa-flip-horizontal" />
        <span class="audio-player__playback-rate">{{ recording.playbackRate ? recording.playbackRate + 'x' : '1x' }}</span>
      </a>

      <span class="audio-player__remaining-time">
        - {{ formatTime(Math.floor(duration) - Math.floor(currentTime)) }}
      </span>

      <a
        :href="audioInfo.recordingUrl" 
        target="_blank" 
        title="Download"
        class="btn btn-function pull-right m-l-10 m-t-2"
        download
      >
        <i class="fa fa-download f-s-13 p-r-0" />
      </a>

      <a
        v-if="audioInfo.hasTranscription" 
        title="View Transcription" 
        href="javascript:void(0);"
        class="btn btn-function pull-right m-l-10 m-t-2"
        @click="viewTranscription"
      >
        <i class="fa fa-file-text-o f-s-13 p-r-0" />
      </a>

      <input
        v-model="recording.currentTime"
        type="range"
        :step="duration/100"
        min="0"
        :max="duration"
        class="m-t-10"
      >
    </div>
  </div>
</template>

<script>

import locales from './locales';
import sharedMessages from '@/core/locales';

export default {
  name: 'AudioPlayer',
  components: {
  },
  props: {
    audioInfo: {
      type: Object,
      default: null
    },
    fixedPlayer: {
      type: Boolean,
      default: false
    },
    noFloat: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      recording: {},
      currentTime: 0,
      duration: 0,
      paused: false,
      forwardBackwardInterval: null
    };
  },
  computed: {
    audioPlayerPosition() {
      return {
        'audio-player': true,
        'audio-player-fixed': this.fixedPlayer,
        'float-none': this.noFloat
      };
    }
  },
  watch: {
    audioInfo: {
      immediate: true,
      handler(newValue) {
        this.recording.src = '';
        this.recording.currentTime = 0;
        this.recording = new Audio(newValue.recordingUrl);
        this.recording.addEventListener('timeupdate', () => {
          this.currentTime = this.recording.currentTime;
          this.duration = this.recording.duration;
          if (this.currentTime - this.duration === 0) {
            this.recording.pause();
            this.paused = true;
          }
        }, false);
        this.recording.play();
      }
    }
  },
  beforeDestroy() {
    this.hidePlayer();
  },
  methods: {
    setPlaybackRate() {
      if (!this.recording.paused) {
        switch (this.recording.playbackRate) {
        case undefined:
        case 1:
          this.recording.playbackRate = 1.2;
          break;
        case 1.2:
          this.recording.playbackRate = 1.5;
          break;
        case 1.5:
          this.recording.playbackRate = 2;
          break;
        case 2:
          this.recording.playbackRate = 3;
          break;
        case 3:
          this.recording.playbackRate = 0.5;
          break;
        case 0.5:
          this.recording.playbackRate = 1;
          break;
        default:
          break;
        }
      }
    },
    fastForwardOrBackward(direction, click) {
      if (!this.recording.paused) {
        if (click) {
          setTimeout(() => {
            this.recording.currentTime = this.recording.currentTime + (direction === 'forward' ? 10 : -10);
            this.recording.play();
          }, 200);
        } else {
          this.recording.pause();
          this.forwardBackwardInterval = setInterval(() => {
            this.recording.currentTime = this.recording.currentTime + (direction === 'forward' ? 10 : -10);
          }, 200);
        }
      }
    },
    resumePlay() {
      if (this.forwardBackwardInterval) {
        clearInterval(this.forwardBackwardInterval);
        this.forwardBackwardInterval = null;
        this.recording.play();
      }
    },
    pause() {
      if (this.recording.currentTime > 0 && !this.recording.paused) {
        this.recording.pause();
        this.paused = true;
      }
    },
    play() {
      if (this.currentTime - this.duration === 0) {
        this.currentTime = 0;
        this.recording.currentTime = 0;
      }
      this.recording.play();
      this.paused = false;
    },
    formatTime(s) {
      return s ? ((s - (s %= 60)) / 60 + (9 < s ? ':' : ':0') + s) : '0:00';
    },
    hidePlayer() {
      this.recording.pause();
      this.recording.src = '';
      this.recording.currentTime = 0;
      this.$emit('hidePlayer');
    },
    resetPlayer() {
      this.recording.src = {};
      this.currentTime = 0;
      this.duration = 0;
      this.paused = false;
      this.forwardBackwardInterval = null;
    },
    viewTranscription() {
      this.$emit('viewTranscription');
    }
  },
  i18n: {
    locale: 'en',
    messages: locales,
    sharedMessages
  },
};
</script>